import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductView } from '../../api/generated/models/ProductView';
import { helper } from '../../utils/helper';
import LicenseCancelModal from './LicenseCancelModal';

interface LicenseAddonCardProps {
  product: ProductView;
  /** Main plugin name of the page (e.g. `content_master`) - used by the cancel modal to refetch the catalog. */
  licenseType: string;
  onPurchase: (product: ProductView) => void;
  hasActiveLicense?: boolean;
  activeLicenseId?: string;
  isCancelled?: boolean;
  expirationDate?: string | null;
  isPaymentPending?: boolean;
  isSubuser?: boolean;
  /**
   * Add-on purchase requires an active main-plugin subscription - the backend
   * grants feature access only when BOTH subscriptions are active.
   */
  canPurchase?: boolean;
}

/**
 * Compact card for add-on subscriptions (e.g.
 * `content_master_addon_marketplace_listing`), rendered under the main plan
 * cards: eyebrow label, name, description, then price + buy/cancel button in
 * the footer. Purchase and cancel reuse the same flows as the plan cards.
 */
const LicenseAddonCard: React.FC<LicenseAddonCardProps> = ({
  product,
  licenseType,
  onPurchase,
  hasActiveLicense = false,
  activeLicenseId,
  isCancelled = false,
  expirationDate,
  isPaymentPending = false,
  isSubuser = false,
  canPurchase = false,
}) => {
  const { t } = useTranslation();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const durationMonths = Math.round(product.durationSec / (30 * 24 * 60 * 60));
  const isYearly = durationMonths >= 12;
  const price = helper.formatPrice({ amount: product.priceCents, currency: 'EUR' });
  const periodLabel = isYearly ? t('year') : t('month');

  return (
    <div className="license-addon-card">
      <span className="license-addon-card-badge">{t('cards_section.card.addon_badge')}</span>
      <h4 className="license-addon-card-title">{product.name}</h4>
      {product.description && (
        <p className="license-addon-card-description">{product.description}</p>
      )}

      <div className="license-addon-card-footer">
        <span className="license-addon-card-price">
          {price}/{periodLabel}
        </span>

        {hasActiveLicense && isCancelled && expirationDate && (
          <button className="license-card-cancelled-info license-card-button free" disabled={true}>
            <span>{t('cards_section.card.cancelled_valid_until', { date: new Date(expirationDate).toLocaleDateString() })}</span>
          </button>
        )}

        {hasActiveLicense && !isCancelled && !isSubuser && (
          <button
            className="license-card-button cancel"
            onClick={() => setIsCancelModalOpen(true)}
          >
            <span>{t('cards_section.card.button_text_cancel')}</span>
          </button>
        )}

        {!hasActiveLicense && (
          <button
            className="license-card-button addon-outline"
            onClick={() => onPurchase(product)}
            disabled={isPaymentPending || !canPurchase}
          >
            {isPaymentPending
              ? t('license_payment_processing')
              : t('cards_section.card.button_text_add_addon')}
          </button>
        )}
      </div>

      {/* Cancel Add-on Modal - same flow as regular subscriptions */}
      {activeLicenseId && (
        <LicenseCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          licenseId={activeLicenseId}
          licenseName={product.name}
          licenseType={licenseType}
          expirationDate={expirationDate}
        />
      )}
    </div>
  );
};

export default LicenseAddonCard;
